import {Injectable} from '@angular/core';
import {AuthConfig, OAuthService} from 'angular-oauth2-oidc';
import {Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {environment} from "../../environments/environment";
import {PermissionContextService} from "./context/permission-context.service";

const baseHref = document.querySelector('base')?.getAttribute('href') ?? '/';
const appBaseUrl = new URL(baseHref, window.location.origin).toString();
export const authConfig: AuthConfig = {
  issuer: environment.issuer,
  clientId: 'web-pkce',
  redirectUri: new URL('callback', appBaseUrl).toString(),
  postLogoutRedirectUri: new URL('login', appBaseUrl).toString(),
  responseType: 'code',
  scope: 'openid profile email',
  requireHttps: environment.isProduction,
  showDebugInformation: true,
  disablePKCE: false,
};

@Injectable({
  providedIn: 'root'
})

export class AuthService {
  private redirectUrl: string = '/'

  constructor(
    public oauthService: OAuthService,
    private router: Router,
    private http: HttpClient,
    private permissionContext: PermissionContextService
  ) {
    this.configureOAuthService();
  }

  private configureOAuthService() {
    this.oauthService.setStorage(sessionStorage)
    this.oauthService.configure(authConfig);
    this.oauthService.setupAutomaticSilentRefresh();
  }

  async login() {
    await this.oauthService.loadDiscoveryDocument();
    const redirectUrl = this.getRedirectUrl() || '/';
    const state = this.encodeState(redirectUrl);
    this.oauthService.initCodeFlow(state, {redirectUri: authConfig.redirectUri, scopes: authConfig.scope,});
  }

  private encodeState(redirectUrl: string): string {
    try {
      return btoa(JSON.stringify({
        redirectUrl,
        nonce: this.generateNonce(),
        timestamp: Date.now()
      }));
    } catch {
      return '';
    }
  }

  private generateNonce(length = 32): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from(crypto.getRandomValues(new Uint8Array(length)))
      .map(x => charset[x % charset.length])
      .join('');
  }

  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  getRedirectUrl(): string {
    return this.redirectUrl;
  }

  handlePostLogin() {
    try {
      const state = this.oauthService.state;
      let redirectUrl = '/';
      if (state) {
        const parsed = this.decodeState(state);
        redirectUrl = this.sanitizeUrl(parsed.redirectUrl);
      }
      this.router.navigateByUrl(redirectUrl);
    } catch (error) {
      this.router.navigateByUrl('/');
    }
  }

  private sanitizeUrl(url: string): string {
    if (url.startsWith('/') && !url.startsWith('//')) {
      return url;
    }
    return '/';
  }


  logout() {
    this.permissionContext.clear()
    // const token = this.oauthService.getAccessToken();
    // const body = new URLSearchParams({token, token_type_hint: 'access_token'});
    //
    // this.http.post('https://elearning-auth.carvia-test.org/revoke', body.toString(), {
    //   headers: {'Content-Type': 'application/x-www-form-urlencoded', 'Authorization': `Bearer ${token}`},
    //   responseType: 'text'
    // }).subscribe({
    //   next: res => {
    //     // this.router.navigate(['/login'])
    //   },
    //   error: err => console.error('Manual revocation error:', err)
    // });

    // if (this.oauthService.revocationEndpoint && this.oauthService.revocationEndpoint.includes('/revoke')) {
    //   this.oauthService.revokeTokenAndLogout({ignoreCorsIssues: true})
    //     .then(() => {
    //       this.router.navigate(['/login']);
    //     })
    //     .catch(err => {
    //       console.error('Revoke failed:', err);
    //     });
    // }
    this.oauthService.logOut();
    if (!this.oauthService.hasValidAccessToken() && !this.oauthService.hasValidIdToken()) {
      this.router.navigate(['/login']);
    }
  }

  getToken(): string {
    return this.oauthService.getAccessToken()
  }

  isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  getProfile() {
    return this.oauthService.getIdentityClaims()
  }

  private decodeState(encodedState: string): any {
    try {
      const urlDecoded = decodeURIComponent(encodedState);
      let base64 = urlDecoded
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      while (base64.length % 4) {
        base64 += '=';
      }
      const json = atob(base64);
      return JSON.parse(json);
    } catch (error) {
      throw new Error('Invalid state parameter');
    }
  }

}
